import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, distinctUntilChanged, from, map, switchMap, timeout } from 'rxjs';
import { FeatureFlagLocalOverride } from 'src/app/core/constants/feature-flag-local-override.constants';
import { FEATURE_FLAG_REQUEST_TIMEOUT_IN_MS } from 'src/app/core/constants/feature-flag.constants';
import { FeatureFlagResponse } from 'src/app/core/models/feature-flag.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
/**
 * This purpose of this service is to fetch the feature flag from the Feature Flag proxy service.
 *
 * This is NOT meant to be used directly and should only be accessed by the FeatureFlagEffects.
 */
export class FeatureFlagStoreDataService {
  private isUserLoggedIn = false;

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private http: HttpClient,
  ) {
    this.authService.userAuthStatusSubject.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      this.isUserLoggedIn = isLoggedIn;
    });
  }

  /**
   * Default attributes. A map of custom key-value string pairs specifying attributes for the user that are used for audience targeting and results segmentation.
   * Platform - indicates if a user is a mobile,web or hybrid user.
   * .      Values - 'mobile' || 'web' || 'mobile_and_web'.
   * Source - tells you where the request came from.
   * .      Values - 'mobile' || 'web'.
   * Environment - indicates the environment in which the code was executed.
   * .      Values - 'production' || 'uat' || 'development'.
   * Object - Used for type of experiment/feature.
   * .      Values - 'User' || 'Job'.
   * Current_target - indicate the type of user.
   * .      Values - 'client' || 'worker' || 'coordinator' || 'admin'.
   * State - indicate the state the user lives in.
   * .      Values - 'Tasmania' || 'Victoria' || 'South Australia' || 'Western Australia' || 'Australian Capital Territory' || 'Queensland' || 'Northern Territory' || 'New South Wales'.
   * Uid - indicate the token UID
   * .      Values - the user_uid if user is logged in, otherwise the generated ID.
   * .      Leaving it for backwards compatibility with Optimizely.
   * Uuid - indicate the token UUID
   * .      Values - the user_uid if user is logged in, otherwise the generated ID.
   */
  private get defaultAttributes(): any {
    return {
      source: 'mobile',
      environment: environment.environmentName,
      object: 'User',
      current_target: (this.accountService.user?.accountType || '').toLowerCase(),
      state: this.accountService.user?.state || '',
      uid: this.accountService.user?.uid || this.accountService.uuid,
      uuid: this.accountService.uuid,
    };
  }

  /**
   * Get userUid. Its exposed through a method as we need to check if the environment is ready.
   */
  async getFeatureFlagUserID(): Promise<string> {
    await this.checkIfReady();
    return this.accountService.uuid;
  }

  /**
   * Wait for dependent data like accounts and user details to load.
   */
  private async checkIfReady(): Promise<void> {
    try {
      this.isUserLoggedIn && (await this.accountService.accountDataReady());
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Maps the response status to the request feature_keys.
   *
   * @param featureKeys - The request parameters that needs to be filled with default attributes.
   * @param response - The list of feature flag response from the backend.
   * @returns An object containing the feature flag key as the key and status as the value.
   */
  private mapResponseStatusToRequestKeys<K extends string>(
    featureKeys: K[],
    response: FeatureFlagResponse[],
  ): Record<K, boolean> {
    return featureKeys.reduce(
      (currentValue, key: string) => {
        const featureFlag = response?.find((flag) => flag.feature_key === key);
        let featureFlagStatus = featureFlag ? featureFlag.status : false;

        // Overriding feature flags in local environment.
        if (environment.environmentName === 'development' && key in FeatureFlagLocalOverride) {
          featureFlagStatus = FeatureFlagLocalOverride[key];
        }

        return {
          ...currentValue,
          [key]: featureFlagStatus,
        };
      },
      {} as Record<K, boolean>,
    );
  }

  /**
   * Sends the feature flag check API request to the backend.
   *
   * @param body - The request body in JSON format. This is of type any as it may contain random properties from the attributes.
   * @returns - The Response or ErrorResponse from the server.
   */
  private sendAPIRequest(body: object): Observable<any> {
    return this.http.post(`${environment.serviceUrl()}/featureflagproxy/api/feature_flags/check_enabled`, body);
  }

  /**
   * DO NOT CALL THIS FUNCTION DIRECTLY! Please use the FeatureFlagFacade.isFeatureEnabled() to retrieve the Feature Flags.
   *
   * Fetch feature flag status from the Feature Flag proxy. This function will return an object with the specified feature flag keys
   * as the object keys and the status as values.
   *
   * Please take note that if the request fails, all the statuses will be set to FALSE.
   *
   * @param featureKeys - The list if feature flags to check.
   * @param attributes - The feature flag attributes. Using type any for attributes argument as it may contain random keys and values.
   * @returns Object with the feature keys as the key and the feature flag enabled status as value.
   */
  fetchFeatureFlags<K extends string>(featureKeys: K[], attributes?: any): Observable<Record<K, boolean>> {
    const proxyRequest$ = () =>
      this.sendAPIRequest({
        feature_flags: featureKeys,
        attributes: {
          ...this.defaultAttributes,
          ...attributes,
        },
      }).pipe(
        timeout(FEATURE_FLAG_REQUEST_TIMEOUT_IN_MS),

        map(({ enabled_status }) => this.mapResponseStatusToRequestKeys(featureKeys, enabled_status)),

        // If there's an error, pass an empty array to set each flag status to FALSE
        catchError((error) => {
          // Observability: Add monitoring for feature flag proxy failure.
          console.error(error, {
            module: 'Feature Flag: FeatureFlagStoreDataService.fetchFeatureFlags',
            featureKeys,
            attributes,
          });

          throw error;
        }),
      );

    // checkIfReady() is needed to get the correct user UUID before sending the request
    return from(this.checkIfReady()).pipe(switchMap(() => proxyRequest$()));
  }
}
